import {EditOutlined} from '@ant-design/icons/lib';
import {Button, Card, Space} from 'antd';
import {UpdateMasterAccountInput, useUpdateMasterAccountMutation} from 'api/generated';
import {AnchorLink, Hint, PropertyList, Stack} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, Input, Switch} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {formatBoolean} from 'lib/formaters';
import {omitEmpty} from 'lib/omitEmpty';
import {
  validateCommaOrNewlineSeparatedDomains,
  validateCommaOrNewlineSeparatedIPs,
  validateCommaSeparatedEmails
} from 'lib/validation';
import React, {useState} from 'react';
import * as yup from 'yup';

// Helper function to parse comma-separated or newline-separated strings
const parseCommaOrNewlineSeparatedString = (value: string | undefined): string[] => {
  if (!value) return [];
  return value.split(/[\s*,\s*\n]+/).filter(Boolean);
};

export const MasterAccountSettings: React.FC<any> = ({cardProps, formProps, masterAccount}) => {
  const [isEditing, setEditing] = useState(false);
  const {isAdmin} = useAccessLevel();
  const [updateMasterAccount] = useUpdateMasterAccountMutation();

  if (!masterAccount) return <Card {...cardProps} title="Settings" />;

  const initialValues: UpdateMasterAccountInput & {
    webhookEmailsString: string;
    churnedEmailsString: string;
    allowedIPsString: string;
    allowedDomainsString: string;
  } = {
    id: masterAccount.id,
    webhookEmailsString: masterAccount.webhookEmails ? masterAccount.webhookEmails.join(', ') : '',
    churnedEmailsString: masterAccount.churnedEmails ? masterAccount.churnedEmails.join(', ') : '',
    allowedIPsString: masterAccount.allowedIPs ? masterAccount.allowedIPs.join(', ') : '',
    allowedDomainsString: masterAccount.allowedDomains
      ? masterAccount.allowedDomains.join(', ')
      : '',
    requireExternalId: masterAccount.requireExternalId || false,
    contractTemplateId: masterAccount.contractTemplateId || '',
    autoApprove: masterAccount.autoApprove || false,
    allowCreateApiKey: masterAccount.allowCreateApiKey || false
  };

  const validationSchema = yup.object().shape({
    webhookEmailsString: yup
      .string()
      .test('valid-emails', 'Invalid email format', (value) => validateCommaSeparatedEmails(value)),
    churnedEmailsString: yup
      .string()
      .test('valid-emails', 'Invalid email format', (value) => validateCommaSeparatedEmails(value)),
    allowedIPsString: yup
      .string()
      .test('valid-ips', 'Invalid IP format', (value) => validateCommaOrNewlineSeparatedIPs(value)),
    allowedDomainsString: yup
      .string()
      .test('valid-domains', 'Invalid domain format', (value) =>
        validateCommaOrNewlineSeparatedDomains(value)
      ),
    contractTemplateId: yup.string(),
    requireExternalId: yup.boolean(),
    autoApprove: yup.boolean(),
    allowCreateApiKey: yup.boolean()
  });

  const handleSubmit = async (
    input: UpdateMasterAccountInput & {
      webhookEmailsString: string;
      churnedEmailsString: string;
      allowedIPsString: string;
      allowedDomainsString: string;
    }
  ) => {
    await updateMasterAccount({
      variables: {
        input: omitEmpty({
          id: masterAccount.id,
          webhookEmails: parseCommaOrNewlineSeparatedString(input.webhookEmailsString),
          churnedEmails: parseCommaOrNewlineSeparatedString(input.churnedEmailsString),
          allowedIPs: parseCommaOrNewlineSeparatedString(input.allowedIPsString),
          allowedDomains: parseCommaOrNewlineSeparatedString(input.allowedDomainsString),
          requireExternalId: input.requireExternalId,
          contractTemplateId: input.contractTemplateId,
          autoApprove: input.autoApprove,
          allowCreateApiKey: input.allowCreateApiKey
        })
      }
    });
    setEditing(false);
  };

  const webhookEmailsLabel = (
    <Hint message="Emails to send notifications when a webhook delivery fails">Webhook Emails</Hint>
  );

  const churnedEmailsLabel = (
    <Hint message="Emails to send notifications when a customer churns">Churned Emails</Hint>
  );

  const allowedIPsLabel = (
    <Hint message="IP addresses allowed to access the API. Leave empty to allow any IP.">
      Allowed IPs
    </Hint>
  );

  const allowedDomainsLabel = (
    <Hint message="Additional domains where MONEI UI Components can be embedded. Apple Pay will be automatically supported on these domains.">
      Allowed Domains
    </Hint>
  );

  const contractTemplateIdLabel = (
    <Hint message="The ID of the contract template to use for this master account.">
      Contract Template ID
    </Hint>
  );

  const requireExternalIdLabel = (
    <Hint
      message={
        <>
          If enabled, the external ID (<code>mid</code> parameter) becomes mandatory in the
          registration link for new accounts. See{' '}
          <a
            href="https://docs.monei.com/monei-connect/#registration-link"
            target="_blank"
            rel="noopener noreferrer">
            docs
          </a>
          .
        </>
      }>
      Require External ID
    </Hint>
  );

  const autoApproveLabel = (
    <Hint message="Automatically approve accounts created under this master account.">
      Auto Approve
    </Hint>
  );

  const allowCreateApiKeyLabel = (
    <Hint message="Allow accounts associated with this master account to create API keys">
      Allow Create API Key
    </Hint>
  );

  return (
    <Card
      {...cardProps}
      title="Settings"
      extra={
        isEditing || !isAdmin ? null : (
          <AnchorLink onClick={() => setEditing(true)}>
            <EditOutlined /> Edit data
          </AnchorLink>
        )
      }>
      {isEditing ? (
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize>
          {({isSubmitting, isValid, submitForm}) => {
            return (
              <Form {...formProps}>
                <FormItem hasFeedback={false} name="webhookEmailsString" label={webhookEmailsLabel}>
                  <Input.TextArea
                    name="webhookEmailsString"
                    placeholder="Please write the emails, separated by commas"
                  />
                </FormItem>
                <FormItem hasFeedback={false} name="churnedEmailsString" label={churnedEmailsLabel}>
                  <Input.TextArea
                    name="churnedEmailsString"
                    placeholder="Please write the emails, separated by commas"
                  />
                </FormItem>
                <FormItem hasFeedback={false} name="allowedIPsString" label={allowedIPsLabel}>
                  <Input.TextArea
                    name="allowedIPsString"
                    placeholder="Please write the allowed IPs, separated by comma or newline"
                  />
                </FormItem>
                <FormItem
                  hasFeedback={false}
                  name="allowedDomainsString"
                  label={allowedDomainsLabel}>
                  <Input.TextArea
                    name="allowedDomainsString"
                    placeholder="Please write the allowed domains, separated by comma or newline"
                  />
                </FormItem>
                <FormItem
                  hasFeedback={false}
                  name="contractTemplateId"
                  label={contractTemplateIdLabel}>
                  <Input
                    name="contractTemplateId"
                    placeholder="Please enter the contract template ID"
                  />
                </FormItem>
                <FormItem
                  hasFeedback={false}
                  name="requireExternalId"
                  label={requireExternalIdLabel}>
                  <Switch name="requireExternalId" />
                </FormItem>
                <FormItem hasFeedback={false} name="autoApprove" label={autoApproveLabel}>
                  <Switch name="autoApprove" />
                </FormItem>
                <FormItem
                  name="allowCreateApiKey"
                  label={
                    <Space>
                      <span>Allow create api key</span>
                      <Hint message="Allow accounts associated with this master account to create API keys" />
                    </Space>
                  }>
                  <Switch name="allowCreateApiKey" />
                </FormItem>
                <FormItem
                  wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
                  name="submit">
                  <Stack>
                    <Button onClick={() => setEditing(false)}>Cancel</Button>
                    <Button
                      type="primary"
                      onClick={submitForm}
                      disabled={isSubmitting || !isValid}
                      loading={isSubmitting}>
                      Submit
                    </Button>
                  </Stack>
                </FormItem>
              </Form>
            );
          }}
        </Formik>
      ) : (
        <PropertyList
          properties={[
            {
              label: webhookEmailsLabel,
              value:
                (masterAccount.webhookEmails ?? []).length > 0
                  ? masterAccount.webhookEmails?.join(', ')
                  : null
            },
            {
              label: churnedEmailsLabel,
              value:
                (masterAccount.churnedEmails ?? []).length > 0
                  ? masterAccount.churnedEmails?.join(', ')
                  : null
            },
            {
              label: allowedIPsLabel,
              value:
                (masterAccount.allowedIPs ?? []).length > 0
                  ? masterAccount.allowedIPs?.join(', ')
                  : null
            },
            {
              label: allowedDomainsLabel,
              value:
                (masterAccount.allowedDomains ?? []).length > 0
                  ? masterAccount.allowedDomains?.join(', ')
                  : null
            },
            {
              label: contractTemplateIdLabel,
              value: masterAccount.contractTemplateId
            },
            {
              label: requireExternalIdLabel,
              value: formatBoolean(masterAccount.requireExternalId)
            },
            {
              label: autoApproveLabel,
              value: formatBoolean(masterAccount.autoApprove)
            },
            {
              label: allowCreateApiKeyLabel,
              value: formatBoolean(masterAccount.allowCreateApiKey)
            }
          ]}
        />
      )}
    </Card>
  );
};
